/**
 * hauler_place_notes — #45 / DEC-359 (migration 0183). A hauler's own notes for drivers about
 * one place: its site (hauler_locations), a Paelode catalog well, or a Paelode catalog
 * disposal facility. Per hauler: wells and facilities are shared platform rows, so the note
 * lives here, tenant-scoped. See the migration header for the rules; the API reads and
 * writes it with raw SQL, and this mirror keeps schema-diff tooling aligned.
 */

import { sql } from 'drizzle-orm';
import {
  check,
  foreignKey,
  index,
  pgPolicy,
  pgTable,
  text,
  timestamp,
  unique,
  uuid,
} from 'drizzle-orm/pg-core';
import { appUserRole } from './enums.js';
import { disposalFacilities } from './disposals.js';
import { haulerLocations, wells } from './platform-catalog.js';
import { importBatches } from './imports.js';
import { tenants, users } from './users.js';

export const haulerPlaceNotes = pgTable(
  'hauler_place_notes',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    haulerLocationId: uuid('hauler_location_id').references(() => haulerLocations.id, {
      onDelete: 'cascade',
    }),
    wellId: uuid('well_id').references(() => wells.id, { onDelete: 'restrict' }),
    disposalFacilityId: uuid('disposal_facility_id').references(() => disposalFacilities.id, {
      onDelete: 'restrict',
    }),
    notes: text('notes').notNull(),
    createdBy: uuid('created_by').references(() => users.id, { onDelete: 'set null' }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedBy: uuid('updated_by').references(() => users.id, { onDelete: 'set null' }),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    // 0253 (OPEN-161) — the import provenance marker: set when the Wells import creates the note,
    // cleared when a dispatcher edits it, so an import undo removes only what the batch created.
    importBatchId: uuid('import_batch_id'),
  },
  (t) => [
    check(
      'hauler_place_notes_one_place',
      sql`num_nonnulls(hauler_location_id, well_id, disposal_facility_id) = 1`,
    ),
    check(
      'hauler_place_notes_notes_length',
      sql`length(btrim(notes)) > 0 AND length(notes) <= 2000`,
    ),
    unique('hauler_place_notes_location_key').on(t.tenantId, t.haulerLocationId),
    unique('hauler_place_notes_well_key').on(t.tenantId, t.wellId),
    unique('hauler_place_notes_facility_key').on(t.tenantId, t.disposalFacilityId),
    index('hauler_place_notes_location_idx').on(t.haulerLocationId),
    index('hauler_place_notes_well_idx').on(t.wellId),
    index('hauler_place_notes_facility_idx').on(t.disposalFacilityId),
    index('hauler_place_notes_import_batch_idx')
      .on(t.tenantId, t.importBatchId)
      .where(sql`import_batch_id IS NOT NULL`),
    // ON DELETE SET NULL ("import_batch_id") in the migration (Postgres 15 column list); 0253 is
    // authoritative, as 0107 is for the import tables.
    foreignKey({
      columns: [t.tenantId, t.importBatchId],
      foreignColumns: [importBatches.tenantId, importBatches.id],
      name: 'hauler_place_notes_import_batch_tenant_fk',
    }).onDelete('set null'),
    pgPolicy('hauler_place_notes_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
      withCheck: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type HaulerPlaceNote = typeof haulerPlaceNotes.$inferSelect;
